import * as React from 'react';
import { Invoice, FilespaceLineItem, FilespaceUsage } from 'fs-api-client';
import FilespaceBreakdownBase, { FilespaceBreakdownBaseProperties } from './FilespaceBreakdownBase';
import { CircledCrossIcon, CheckBoldIcon } from './common/Icons';
import { downloadCSV, formatDate } from './billing/breakdown/BreakdownModalHelpers';

interface FilespaceBreakdownProps extends FilespaceBreakdownBaseProperties {
    invoice: Invoice | null;
}

interface FilespaceBreakdownState {
    currentInvoiceIdx: number;
    showAllFilespacesPerInvoice: boolean;
}

const LESS_FILESPACES_COUNT = 3;

export class FilespaceBreakdownModal extends FilespaceBreakdownBase<FilespaceBreakdownProps, FilespaceBreakdownState> {
    constructor (props: FilespaceBreakdownProps) {
        super(props);
        this.state = {
            currentInvoiceIdx: 0,
            showAllFilespacesPerInvoice: false
        };
    }
    render() {
        const currentInvoice = this.props.invoice;
        if (!currentInvoice) {
            return null;
        }
        const filespaceLineItems = currentInvoice.filespaceBreakdown ? currentInvoice.filespaceBreakdown : [];

        const periodStart = currentInvoice.periodStart ? new Date(currentInvoice.periodStart) : new Date();
        const periodEnd = currentInvoice.periodEnd ? new Date(currentInvoice.periodEnd) : new Date();

        return (
            <div className="modal fade modal-wider" id="filespaceBreakdownModal" tabIndex={-1} role="dialog" aria-labelledby="filespaceBreakdownModal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Invoice breakdown per filespace
                            </h5>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">
                                    <CircledCrossIcon width="20px" height="20px" />
                                </span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="table-container active">
                                <span className="box__label">
                                    <strong>{formatDate(periodStart)} - {formatDate(periodEnd)}</strong>
                                    <div className="table-tools">
                                        <button className="btn-flex-light" onClick={() => downloadCSV(this.props.invoice)}>Export in CSV</button>
                                    </div>
                                </span>
                                {this.renderFilespaces(filespaceLineItems)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFilespaces(filespaceLineItems: FilespaceLineItem[]) {
        let filespacesRendered = 0;
        const filespacesShowHideText = this.state.showAllFilespacesPerInvoice ? 'View less filespaces' : 'View all filespaces';
        const hasMoreFilespaces = filespaceLineItems.length > LESS_FILESPACES_COUNT;
        const columnsHeaders = this.getTableHeaders(filespaceLineItems);

        return (
            <table className="table">
                <thead>
                    <tr>
                        <th scope="col" className="hidden left">Plan</th>
                        <th scope="col" className="left long">Filespace name</th>
                        <th scope="col" className={`hidden center`}>LucidLink Storage</th>
                        {columnsHeaders.map(header => {
                            if (header === FilespaceUsage.DimensionEnum.Storage) {
                                return null;
                            }
                            return (<th scope="col" key={header} className={`hidden ${header === FilespaceUsage.DimensionEnum.Storage ? ' center' : ''}`}>{this.getDescriptionForDimension(header)}</th>); }
                        )}
                        <th scope="col">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {filespaceLineItems.map(fs => {
                        ++filespacesRendered;
                        if (!this.state.showAllFilespacesPerInvoice && filespacesRendered > LESS_FILESPACES_COUNT) {
                            return null;
                        }
                        return this.renderSingleFilespace(fs, columnsHeaders);
                    })}
                    {hasMoreFilespaces && (<tr>
                        <td colSpan={5} onClick={() => this.onShowMoreFilespacesClicked()} className="table-controler active">{filespacesShowHideText}</td>
                    </tr>)}
                </tbody>
            </table>);
    }

    renderSingleFilespace(filespaceLineItem: FilespaceLineItem, columnOrder: FilespaceUsage.DimensionEnum[]) {
        const rowClassName = filespaceLineItem.isDeleted ? 'deleted' : undefined;
        return (
            <tr className={rowClassName}>
                {this.renderStorageType(filespaceLineItem)}
                <td className="left">
                    <strong>{filespaceLineItem.name}</strong>
                    {filespaceLineItem.isDeleted && <span>deleted</span>}
                </td>
                {filespaceLineItem.storageOwner === FilespaceLineItem.StorageOwnerEnum.Lucidlink ?
                    (<td key={filespaceLineItem.name + FilespaceUsage.DimensionEnum.Storage} className="hidden green center">
                        <CheckBoldIcon width="15px" />
                    </td>) : (<td key={filespaceLineItem.name + FilespaceUsage.DimensionEnum.Storage}className="hidden"/>)
                }
                {columnOrder.filter(column => column !== FilespaceUsage.DimensionEnum.Storage).map((column: FilespaceUsage.DimensionEnum) => {
                    const dimensionUsage = filespaceLineItem.usageByDimension.find(usage => usage.dimension === column);
                    return (<td key={column} className="hidden">
                                {dimensionUsage && this.renderUsage(dimensionUsage.dimension, dimensionUsage.usage)}
                            </td>);
                })}
                <td>${(filespaceLineItem.amountDue / 100).toFixed(2)}</td>
            </tr>);
    }

    onShowMoreFilespacesClicked() {
        this.setState({
            showAllFilespacesPerInvoice: !this.state.showAllFilespacesPerInvoice
        });
    }
}



// WEBPACK FOOTER //
// ./src/webportal/components/FilespaceBreakdownModal.tsx